import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import { useRoutes } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AuthProvider } from './contexts/auth/AuthProvider';
import { isZitadelAuth } from './contexts/auth/zitadelContext';
import { ZitadelLoading } from './pages/auth/zitadel/ZitadelPages';
import { AuthContextProvider } from './contexts/AuthContext';
import { queryClient } from './hooks/api/react-query';
import useTheme from './hooks/useTheme';
import './i18n';
import { store } from './redux/store';
import routes from './routes';
import createTheme from './theme';
import { SkipToMain } from './components/SkipToMain';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

// SFDA-858: loaded only with REACT_APP_AUTH_PROVIDER=zitadel, so Auth0 builds never
// download oidc-client-ts.
const ZitadelAuthProvider = lazy(
  () => import('./contexts/auth/ZitadelAuthProvider')
);
const SessionProvider = isZitadelAuth ? ZitadelAuthProvider : AuthProvider;

function App() {
  const content = useRoutes(routes());

  const { theme } = useTheme();

  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <MuiThemeProvider theme={createTheme(theme)}>
          {/* Suspense: only the lazy Zitadel provider ever suspends here. */}
          <Suspense fallback={isZitadelAuth ? <ZitadelLoading /> : null}>
            <SessionProvider>
              <AuthContextProvider>
                <SkipToMain />
                {content}
              </AuthContextProvider>
            </SessionProvider>
          </Suspense>
        </MuiThemeProvider>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
